/**
 * {@link DataTable} for specialties.
 */
import * as React from 'react';
import { Star } from 'lucide-react';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderSpecialtyDetails } from './renderSpecialtyDetails';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { Button } from '@/components/forms/Button';
import { SpecialtyRow } from './SpecialtyRow';
import { SpecialtyCard } from './SpecialtyCard';
import { SpecialtyFiltersContent } from './SpecialtyFilters';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getSpecialties, deleteSpecialty } from '@/services/specialties.service';
import type { Specialty } from '@/types/specialty.types';
import { cn } from '@/lib/utils';
import {
  DEFAULT_SPECIALTY_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type SpecialtyFilters,
  type FilterGroupId,
} from './specialty-configs';
import { filterSpecialties, hasSpecialtyActiveFilters } from './specialty-table-utils';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link SpecialtiesDataTable}. */
interface SpecialtiesDataTableProps {
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditSpecialty?: (id: string) => void;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean };
  showCreateButton?: boolean;
  onCreateSpecialty?: () => void;
  layoutWidth?: ElementWidth;
}

/** Specialties list / grid with filters, details, and delete. */
export const SpecialtiesDataTable: React.FC<SpecialtiesDataTableProps> = ({
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditSpecialty,
  pageActions,
  showCreateButton = false,
  onCreateSpecialty,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = React.useState<Specialty | null>(null);

  const dt = useDataTable<SpecialtyFilters, FilterGroupId>({
    tableName: 'specialties',
    defaultFilters: DEFAULT_SPECIALTY_FILTERS,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth,
    isSection: false,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: allSpecialties = [], isLoading } = useQuery({
    queryKey: ['specialties', 'all'],
    queryFn: () => getSpecialties(false),
  });

  const deleteMutation = useMutation({
    mutationFn: (specialty: Specialty) => deleteSpecialty(specialty.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['specialties'] });
      setDeleteTarget(null);
      toast.success('Filière supprimée avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const filteredData = React.useMemo(
    () => filterSpecialties(allSpecialties, dt.filters),
    [allSpecialties, dt.filters],
  );

  const hasActiveFilters = React.useMemo(() => hasSpecialtyActiveFilters(dt.filters), [dt.filters]);

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
    },
    [dt],
  );

  const setSearchTerm = React.useCallback(
    (term: string) => {
      dt.setFilters((prev) => ({ ...prev, searchTerm: term || undefined }));
    },
    [dt],
  );

  const activeExpandedRowId = expandedRowId ?? dt.expandedRowId;
  const {
    data: expandedSpecialtyData,
    isLoading: isLoadingExpandedSpecialty,
    isError: isExpandedSpecialtyError,
    error: expandedSpecialtyError,
  } = useQuery({
    queryKey: ['specialty', activeExpandedRowId],
    queryFn: async () => {
      if (!activeExpandedRowId) return null;
      const { getSpecialty } = await import('@/services/specialties.service');
      return await getSpecialty(activeExpandedRowId);
    },
    enabled: !!activeExpandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedSpecialtyError,
    error: expandedSpecialtyError,
    catalogKey: 'specialtyNotFound',
    onClose: onCloseDetails,
    entityId: activeExpandedRowId,
  });

  const expandedSpecialty = expandedSpecialtyData ?? null;

  const filtersContent = React.useMemo(
    () => (
      <SpecialtyFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onSetSearchTerm={setSearchTerm}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, toggleStatusFilter, setSearchTerm],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  return (
    <DataTable
      data={filteredData}
      totalItems={filteredData.length}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={1}
      totalPages={1}
      startIndex={0}
      endIndex={filteredData.length}
      itemLabel="spécialités"
      onPageChange={() => {}}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      toolbarActions={
        <>
          <Button
            preset="toolbar-filter"
            className={cn(dt.showFilters && 'section-action-button-active')}
            showBadge={hasActiveFilters}
            onClick={() => {
              dt.setShowFilters((prev) => {
                if (prev) dt.setShowConfigure(false);
                return !prev;
              });
            }}
          />
          {showCreateButton && onCreateSpecialty && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer une spécialité"
              onClick={onCreateSpecialty}
            />
          )}
        </>
      }
      filtersPanel={
        <DataTableFilters
          show={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={dt.clearFilters}
          resultCount={filteredData.length}
          resultLabel="spécialité(s)"
          onToggleConfigure={() => dt.setShowConfigure((v) => !v)}
          showConfigure={dt.showConfigure}
          configureContent={
            <div className="flex flex-wrap gap-2">
              {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                const isOn = dt.filterVisibility[id];
                return (
                  <Button
                    key={id}
                    variant="custom"
                    size="sm"
                    className={cn(
                      'px-3 py-1.5 text-sm rounded-lg border transition-all',
                      isOn
                        ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                        : 'bg-transparent border-[var(--color-1-nuance-3)] text-muted-foreground hover:border-[var(--color-1-nuance-1)]',
                    )}
                    onClick={() => dt.handleToggleFilterVisibility(id)}
                    disabled={dt.isSavingFilterVisibility}
                  >
                    {FILTER_GROUP_LABELS[id]}
                  </Button>
                );
              })}
            </div>
          }
        />
      }
      renderTableHead={() => (
        <tr>
          <th>Code</th>
          <th>Libellé</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(specialty) => (
        <SpecialtyRow
          specialty={specialty}
          onView={onToggleRow ? () => onToggleRow(specialty.id) : undefined}
          onEdit={onEditSpecialty}
          onDelete={(s) => setDeleteTarget(s)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(specialty) => (
        <DetailsViewContent>{renderSpecialtyDetails(specialty)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(specialty) => (
        <SpecialtyCard
          specialty={specialty}
          onView={onToggleRow ? () => onToggleRow(specialty.id) : undefined}
          onEdit={onEditSpecialty}
          onDelete={(s) => setDeleteTarget(s)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={Star}
      emptyText="Aucune spécialité trouvée"
      title="Spécialités"
      sidebarMenuId="menu-specialties"
      expandedItem={expandedSpecialty}
      isExpandedDetailLoading={isLoadingExpandedSpecialty}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(specialty) => (
        <DetailsViewContent>{renderSpecialtyDetails(specialty)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            if (deleteTarget) deleteMutation.mutate(deleteTarget);
          }}
          itemName={deleteTarget?.label}
          isLoading={deleteMutation.isPending}
          title="Confirmer la suppression"
          description={`Êtes-vous sûr de vouloir supprimer cette spécialité ? Cette action est irréversible.\n\nNotez que vous pouvez également désactiver cette spécialité en cliquant sur « Modifier », puis en décochant le bouton radio « Spécialité active ».`}
          confirmText="Supprimer"
          variant="danger"
        />
      }
    />
  );
};
